import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, track } from "@/context/LanguageContext";
import { BSPORT } from "@/data/config";
import { Logo } from "@/components/Logo";

const links = (t) => [
  { to: "/classes", label: t.nav.classes },
  { to: "/pricing", label: t.nav.pricing },
  { to: "/schedule", label: t.nav.schedule },
  { to: "/events", label: t.nav.events },
  { to: "/about", label: t.nav.about },
  { to: "/corporate", label: t.nav.corporate },
];

function LangSwitch({ light }) {
  const { lang, setLanguage } = useLang();
  const c = light ? "text-petal" : "text-onyx";
  return (
    <div className={`label flex items-center gap-2 ${c}`} data-testid="lang-switch">
      {["en", "nl"].map((l, i) => (
        <span key={l} className="flex items-center gap-2">
          {i > 0 && <span className="opacity-40">/</span>}
          <button
            onClick={() => setLanguage(l)}
            data-testid={`lang-${l}`}
            className={`transition-opacity duration-300 ${lang === l ? "opacity-100" : "opacity-40 hover:opacity-70"}`}
          >
            {l.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );
}

export function Navbar() {
  const { t } = useLang();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = pathname === "/";
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const textColor = transparent ? "text-petal" : "text-onyx";

  return (
    <>
      <header
        className={`fixed top-0 left-0 z-50 w-full transition-colors duration-500 ${
          transparent ? "bg-transparent" : "bg-petal/95 backdrop-blur-sm border-b border-onyx/10"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-12">
          <Link to="/" aria-label="Maison Core — home" className="block" data-testid="logo">
            <Logo color={transparent ? "#FBF8F5" : "#280B03"} className="h-8 w-[150px] md:h-10 md:w-[188px]" />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {links(t).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.to.slice(1)}`}
                className={`label transition-opacity duration-300 hover:opacity-60 ${textColor} ${pathname === l.to ? "opacity-100" : "opacity-80"}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-6">
            <div className="hidden md:block"><LangSwitch light={transparent} /></div>
            <Link
              to="/schedule"
              data-testid="nav-book-cta"
              onClick={() => track("book_class_click", { source: "navbar" })}
              className={`hidden label border px-6 py-3 transition-colors duration-500 sm:inline-block ${
                transparent ? "border-petal text-petal hover:bg-petal hover:text-onyx" : "border-onyx bg-onyx text-petal hover:bg-transparent hover:text-onyx"
              }`}
            >
              {t.nav.book}
            </Link>
            <button
              className={`lg:hidden ${textColor}`}
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="menu-open"
            >
              <Menu strokeWidth={1.2} size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] bg-onyx text-petal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-5">
              <Logo color="#FBF8F5" className="h-7 w-[140px]" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="menu-close">
                <X strokeWidth={1.2} size={28} />
              </button>
            </div>
            <nav className="flex flex-col px-6 pt-8">
              {[...links(t), { to: "/faq", label: t.nav.faq }, { to: "/contact", label: t.nav.contact }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.05, duration: 0.5 }}
                >
                  <Link to={l.to} className="block border-b border-petal/15 py-5 font-serif text-4xl" data-testid={`mnav-${l.to.slice(1)}`}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="absolute bottom-0 left-0 w-full space-y-6 px-6 pb-10">
              <a
                href={BSPORT.INTRO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("intro_offer_click", { source: "mobile-menu" })}
                className="label block border border-petal bg-petal py-4 text-center text-onyx"
                data-testid="mobile-intro-cta"
              >
                {t.nav.intro} · €59
              </a>
              <LangSwitch light />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
